/**
 * TypeScript interfaces for Epicor Connect module
 */

export enum SyncJobType {
  PRODUCTS = 'products',
  CUSTOMERS = 'customers',
  PRICING = 'pricing',
  ORDERS = 'orders',
  ORDER_STATUS = 'order_status'
}

export enum SyncJobStatus {
  SCHEDULED = 'scheduled',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

export interface SyncJobParameters {
  verbose?: boolean;
  quiet?: boolean;
}

export interface ProductSyncParameters extends SyncJobParameters {
  batch_size?: number;
  product_code?: string;
  families_only?: boolean;
}

export interface CustomerSyncParameters extends SyncJobParameters {
  customer_id: string; // Required: specific ID or 'All'
}

export interface PricingSyncParameters extends SyncJobParameters {
  price_list_code?: string;
  page_size?: number;
}

export interface OrderSyncParameters extends SyncJobParameters {
  order_id?: string;
  limit?: number;
  status_only?: boolean;
}

export interface OrderStatusSyncParameters extends SyncJobParameters {
  order_id?: string;
  order_num?: number;
  limit?: number;
  status_only?: boolean;
}

export interface SyncJob {
  id: string;
  job_type: SyncJobType;
  job_name: string;
  description?: string;
  is_scheduled: boolean;
  cron_expression?: string;
  next_run_at?: string;
  last_run_at?: string;
  parameters?: Record<string, any>;
  status: SyncJobStatus;
  is_active: boolean;
  is_overdue: boolean;
  is_running: boolean;
  
  // Last execution results
  last_execution_duration?: number;
  last_execution_records_processed: number;
  last_execution_records_created: number;
  last_execution_records_updated: number;
  last_execution_records_failed: number;
  last_execution_error?: string;
  
  // Metadata
  created_at: string;
  updated_at: string;
}

export interface SyncJobExecution {
  id: string;
  sync_job_id: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  status: SyncJobStatus;
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  error_message?: string;
  // Captured stdout/stderr of the sync tool. Previously read by the
  // executor and discarded, so a failed run could say THAT it failed but
  // never WHY -- diagnosing one meant journalctl on the right host.
  output_log?: string;
  error_log?: string;
  trigger_type: string;
  success_rate: number;
  throughput: number;
}

export interface EpicorEnvironmentInfo {
  environment_name: string;
  base_url: string;
  api_version: string;
  database_company: string;
  connection_status: string;
  last_connection_test?: string;
}

export interface SyncJobStatusOverview {
  job_id: string;
  job_name: string;
  job_type: SyncJobType;
  status: SyncJobStatus;
  last_run_at?: string;
  next_run_at?: string;
  is_overdue: boolean;
  is_running: boolean;
  records_processed: number;
  success_rate: number;
}

export interface EpicorConnectDashboard {
  epicor_environment: EpicorEnvironmentInfo;
  sync_jobs: SyncJobStatusOverview[];
  total_jobs: number;
  active_jobs: number;
  scheduled_jobs: number;
  running_jobs: number;
  failed_jobs: number;
  last_24h_executions: number;
}

export interface SyncJobCreateRequest {
  job_type: SyncJobType;
  job_name: string;
  description?: string;
  is_scheduled: boolean;
  cron_expression?: string;
  parameters?: Record<string, any>;
  is_active: boolean;
}

export interface SyncJobUpdateRequest {
  job_name?: string;
  description?: string;
  is_scheduled?: boolean;
  cron_expression?: string;
  parameters?: Record<string, any>;
  is_active?: boolean;
}

export interface SyncJobExecuteRequest {
  parameters?: Record<string, any>;
  force?: boolean;
}

export interface SyncJobExecutionLog {
  executions: SyncJobExecution[];
  total_count: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface SyncJobScheduleUpdate {
  is_scheduled: boolean;
  cron_expression?: string;
}

export interface CronTemplate {
  [key: string]: string | null;
}

export interface CronTemplatesResponse {
  templates: CronTemplate;
  examples: Record<string, string>;
}

// Form data interfaces for creating/editing sync jobs
export interface SyncJobFormData {
  job_type: SyncJobType;
  job_name: string;
  description: string;
  is_scheduled: boolean;
  cron_expression: string;
  is_active: boolean;
  
  // Parameters by job type
  parameters: {
    // Products
    batch_size?: number;
    product_code?: string;
    families_only?: boolean;
    
    // Customers  
    customer_id?: string;
    
    // Pricing
    price_list_code?: string;
    page_size?: number;
    
    // Orders
    order_id?: string;
    limit?: number;
    status_only?: boolean;
    
    // Order Status
    order_num?: number;
    
    // Common
    verbose?: boolean;
    quiet?: boolean;
  };
}

// UI state interfaces
export interface SyncJobFilters {
  job_type?: SyncJobType;
  status?: SyncJobStatus;
  active_only: boolean;
  scheduled_only: boolean;
}

export interface SyncJobUIState {
  loading: boolean;
  error: string | null;
  selectedJob: SyncJob | null;
  filters: SyncJobFilters;
  showCreateDialog: boolean;
  showExecuteDialog: boolean;
  showScheduleDialog: boolean;
  showExecutionHistory: boolean;
}

// Constants for UI
export const SYNC_JOB_TYPE_LABELS: Record<SyncJobType, string> = {
  [SyncJobType.PRODUCTS]: 'Products Sync',
  [SyncJobType.CUSTOMERS]: 'Customers Sync',
  [SyncJobType.PRICING]: 'Pricing Sync',
  [SyncJobType.ORDERS]: 'Orders Sync',
  [SyncJobType.ORDER_STATUS]: 'Order Status Sync'
};

export const SYNC_JOB_STATUS_LABELS: Record<SyncJobStatus, string> = {
  [SyncJobStatus.SCHEDULED]: 'Scheduled',
  [SyncJobStatus.RUNNING]: 'Running',
  [SyncJobStatus.COMPLETED]: 'Completed',
  [SyncJobStatus.FAILED]: 'Failed',
  [SyncJobStatus.CANCELLED]: 'Cancelled'
};

export const SYNC_JOB_STATUS_COLORS: Record<SyncJobStatus, string> = {
  [SyncJobStatus.SCHEDULED]: '#2196F3', // Blue
  [SyncJobStatus.RUNNING]: '#FF9800',   // Orange
  [SyncJobStatus.COMPLETED]: '#4CAF50', // Green
  [SyncJobStatus.FAILED]: '#F44336',    // Red
  [SyncJobStatus.CANCELLED]: '#9E9E9E'  // Gray
};

export const CRON_EXPRESSION_EXAMPLES = [
  { value: '0 8 * * *', label: 'Daily at 8:00 AM' },
  { value: '0 */4 * * *', label: 'Every 4 hours' },
  { value: '0 */1 * * *', label: 'Every hour' },
  { value: '*/30 * * * *', label: 'Every 30 minutes' },
  { value: '0 8,20 * * *', label: 'Twice daily (8 AM and 8 PM)' },
  { value: '0 8 * * 0', label: 'Weekly on Sunday at 8:00 AM' },
  { value: '', label: 'Manual execution only' }
];